"use client";

import { useTranslations } from "next-intl";
import { Button } from "@/app/components/ui/Button";
import { Checkbox } from "@/app/components/ui/Checkbox";
import { Input } from "@/app/components/ui/Input";
import { Select } from "@/app/components/ui/Select";
import { defaultSalonHours, removeSalonStaff, resolveSalonSelections, validSalonSetup, validSalonStaff, type SalonHours, type SalonStaff } from "./salonSetup";
import { SetupServicesFields } from "./SetupServicesFields";
import type { IndustrySetupFieldsProps } from "./types";

export function SalonSetupFields({ context, input, step = 0, onChange }: IndustrySetupFieldsProps) {
  const t = useTranslations("apps.industrySetup");
  const setup = input.salonSetup;
  const { groups } = resolveSalonSelections(context, input);
  if (!setup) return null;
  const updateStaff = (key: string, patch: Partial<SalonStaff>) => onChange({ salonSetup: {
    ...setup, staff: setup.staff.map((person) => person.key === key ? { ...person, ...patch } : person),
  } });

  return (
    <div className="space-y-6">
      {step === 0 ? <>
        {groups.some((group) => group.options.length > 0) ? (
          <div className="grid gap-4 sm:grid-cols-2">
            {groups.filter((group) => group.options.length > 0).map((group) => (
              <Select key={group.field} label={t(`reuse.fields.${group.field}`)} required value={group.value ?? ""}
                onChange={(event) => onChange({ [group.field]: event.target.value || null, ...group.field === "companyUuid" ? { branchUuid: null } : {} })}>
                <option value="">{t("reuse.choose")}</option>
                {group.options.map((option) => <option key={option.uuid} value={option.uuid}>{option.name}</option>)}
              </Select>
            ))}
          </div>
        ) : null}
        {!input.installOnly ? <>
          <p className="text-sm leading-6 text-text-secondary">{t("hairSalon.teamDescription")}</p>
          {setup.staff.map((person, index) => (
            <fieldset key={person.key} className="space-y-4 rounded-xl border border-border p-5">
              <legend className="px-2 text-sm font-semibold">{t("hairSalon.person", { number: index + 1 })}</legend>
              <div className="flex items-end gap-3">
                <Input className="flex-1" label={t("hairSalon.personName")} maxLength={255} required value={person.name}
                  onChange={(event) => updateStaff(person.key, { name: event.target.value })} />
                <Button type="button" variant="ghost" disabled={setup.staff.length === 1}
                  aria-label={t("hairSalon.removePerson", { number: index + 1 })}
                  onClick={() => onChange({ salonSetup: removeSalonStaff(setup, person.key) })}>{t("hairSalon.remove")}</Button>
              </div>
              <details>
                <summary className="cursor-pointer text-sm font-medium">{t("hairSalon.workingHours")}</summary>
                <p className="my-3 text-xs leading-5 text-text-secondary">{t("hairSalon.hoursDescription")}</p>
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5, 6, 7].map((day) => {
                    const intervals = person.workingHours.filter((hours) => hours.dayOfWeek === day);
                    const updateHours = (next: SalonHours[]) => updateStaff(person.key, {
                      workingHours: [...person.workingHours.filter((hours) => hours.dayOfWeek !== day), ...next],
                    });
                    return <div key={day} className="space-y-2 rounded-lg bg-bg-elevated p-3">
                      <label className="flex items-center gap-2 text-sm">
                        <Checkbox checked={intervals.length > 0} onChange={(event) => updateHours(event.target.checked
                          ? [{ dayOfWeek: day, startTime: "09:00", endTime: "17:00" }] : [])} />
                        {t(`hairSalon.days.${day}`)}
                      </label>
                      {intervals.map((hours, intervalIndex) => <div key={intervalIndex} className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
                        <Input label={t("hairSalon.from")} type="time" required value={hours.startTime}
                          onChange={(event) => updateHours(intervals.map((item, i) => i === intervalIndex ? { ...item, startTime: event.target.value } : item))} />
                        <Input label={t("hairSalon.to")} type="time" required value={hours.endTime}
                          onChange={(event) => updateHours(intervals.map((item, i) => i === intervalIndex ? { ...item, endTime: event.target.value } : item))} />
                        <Button type="button" variant="ghost" aria-label={t("hairSalon.removeHours")}
                          onClick={() => updateHours(intervals.filter((_, i) => i !== intervalIndex))}>{t("hairSalon.remove")}</Button>
                      </div>)}
                      {intervals.length === 1 ? <Button type="button" variant="ghost" size="sm" onClick={() => updateHours([
                        ...intervals, { dayOfWeek: day, startTime: intervals[0].endTime, endTime: "19:00" },
                      ])}>{t("hairSalon.addHours")}</Button> : null}
                    </div>;
                  })}
                </div>
              </details>
            </fieldset>
          ))}
          <Button type="button" variant="secondary" disabled={setup.staff.length >= 30} onClick={() => onChange({ salonSetup: {
            ...setup, staff: [...setup.staff, { key: `staff-${crypto.randomUUID()}`, name: "", workingHours: defaultSalonHours() }],
          } })}>{t("hairSalon.addPerson")}</Button>
          <p className="text-xs text-text-secondary">{t("hairSalon.photosLater")}</p>
          {!validSalonStaff(setup) ? <p className="text-sm text-text-secondary" role="status">{t("hairSalon.teamValidation")}</p> : null}
        </> : null}
      </> : null}

      {step === 1 && !input.installOnly ? <>
        <p className="text-sm leading-6 text-text-secondary">{t("hairSalon.servicesDescription")}</p>
        <SetupServicesFields
          namespace="apps.industrySetup.hairSalon"
          services={setup.services}
          onChange={(services) => onChange({ salonSetup: { ...setup, services } })}
          createService={() => ({ key: `service-${crypto.randomUUID()}`, name: "", durationMinutes: 30, price: null, staffKeys: setup.staff.map((person) => person.key) })}
          renderOptions={(service, updateService) => <fieldset>
            <legend className="mb-2 text-sm font-medium">{t("hairSalon.providedBy")}</legend>
            <div className="flex flex-wrap gap-3">
              {setup.staff.map((person) => <label key={person.key} className="flex items-center gap-2 rounded-lg bg-bg-elevated px-3 py-2 text-sm">
                <Checkbox checked={service.staffKeys.includes(person.key)} onChange={(event) => updateService({
                  staffKeys: event.target.checked ? [...service.staffKeys, person.key] : service.staffKeys.filter((key) => key !== person.key),
                })} />{person.name}
              </label>)}
            </div>
          </fieldset>}
        />
        {!validSalonSetup(input) ? <p className="text-sm text-text-secondary" role="status">{t("hairSalon.servicesValidation")}</p> : null}
      </> : null}

      {step === 2 && !input.installOnly ? <div className="space-y-5">
        <p className="text-sm text-text-secondary">{t("hairSalon.reviewDescription")}</p>
        <div className="rounded-xl bg-primary-subtle/60 p-5">
          <p className="font-semibold">{input.displayName}</p>
          <p className="mt-2 text-sm">{t("hairSalon.summary", { staff: setup.staff.length, services: setup.services.length })}</p>
          <ul className="mt-4 space-y-3 text-sm">
            {setup.services.map((service) => <li key={service.key}>
              <p className="font-medium">{t("hairSalon.serviceSummary", { name: service.name, minutes: service.durationMinutes })}</p>
              <p className="text-text-secondary">{setup.staff.filter((person) => service.staffKeys.includes(person.key)).map((person) => person.name).join(", ")}</p>
              <p className="text-text-secondary">{service.price == null ? t("hairSalon.priceLater") : t("hairSalon.priceSummary", { price: service.price })}</p>
            </li>)}
          </ul>
        </div>
        <p className="text-xs leading-5 text-text-secondary">{t("hairSalon.existingSettings")}</p>
      </div> : null}
    </div>
  );
}
